// Portable world file (`.tfworld`, M29). A single JSONL document that carries a
// world's identity + its edited sections between the browser and a headless host:
// the browser exports one from IndexedDB, `tools/import-world.ts` writes it into a
// server SQLite store, and `WorldHost` then serves it. It is deliberately DOM- and
// three-free (importable by both `src/client/**` and the server-side `tools/**`) and
// dependency-free (hand-validated — an imported file is untrusted input).
//
// Wire shape: line 1 is the header `{kind:'tfworld', …}`; every later line is a
// `{kind:'section', …}` record. Only EDITED sections travel — pristine terrain
// regenerates from the seed on load (the worldgen isomorphism, ENG-1/M26.1), so a
// creative build exports to a small file. Section blocks are run-length encoded
// (`rle` = flat `[count, state, count, state, …]`), the same scheme `saves.ts` uses
// in IndexedDB; an all-air (cleared) overlay is the empty run list `[]`.

/** Bumped only if the on-disk shape changes incompatibly (checked on parse). */
export const WORLDFILE_VERSION = 1;

/** Cells per section edge / per section (16³ = 4096) — a decoded section's length. */
const SECTION_CELLS = 16 * 16 * 16;

/** The subset of world identity needed to reconstruct the world (matches MetaWire). */
export interface WorldFileMeta {
  id: string;
  name: string;
  seed: string;
  worldType: 'normal' | 'flat' | 'atlas' | 'earth';
  gameMode: 'survival' | 'creative';
  difficulty: 'peaceful' | 'easy' | 'normal' | 'hard';
  hardcore: boolean;
  /** Flat-preset id (empty string when not a preset flat world). */
  flatPreset: string;
}

/** World clock snapshot, carried when the exporter has a live world (else null). */
export interface WorldFileClock {
  tickCount: number;
  dayTime: number;
  weather: { raining: boolean; thundering: boolean; rainCountdown: number; thunderCountdown: number };
}

/** One edited section. `states` is the decoded 4096-cell block-state grid, or null
 *  for an all-air overlay (a section that was cleared relative to pristine terrain). */
export interface WorldFileSection {
  dim: string;
  cx: number;
  cz: number;
  sy: number;
  states: Uint16Array | null;
}

/** One block entity (§3.6 — chest contents, a spawner's config, a crop's genome,
 *  …). Carried alongside its section: a shell block (e.g. a chest) round-trips
 *  through `states` like any other block, but what it HOLDS lives only here
 *  (M51.6, D-TF-257 — a lesson template needs a stocked chest to mean anything). */
export interface WorldFileBlockEntity {
  dim: string;
  x: number;
  y: number;
  z: number;
  type: string;
  data: unknown;
}

/** One templated mob spawn (M53.5, ARCH §29.12 — "the lesson's 12 chickens";
 *  D-TF-265: ".tfworld carries no mobs in either host, which M53.5's templates
 *  will have to answer"). A DECLARATION, not a live entity snapshot: `health`
 *  stands in for the species' `maxHealth` (worldfile.ts is pure/registry-free —
 *  ENG-1 — so it cannot look that up itself; the lesson author supplies it,
 *  exactly as a `SavedEntity` row always has carried a concrete `health`). */
export interface WorldFileMob {
  dim: string;
  x: number;
  y: number;
  z: number;
  type: string;
  health: number;
  baby: boolean;
  /** Base64 genome in the species' own gene-table layout, or absent for a
   *  species with no table / a rule-off spawn. */
  genome?: string;
}

export interface WorldFile {
  meta: WorldFileMeta;
  /** null when the source (durable browser storage) had no live clock. */
  clock: WorldFileClock | null;
  sections: WorldFileSection[];
  /** Absent/empty on every pre-M51.6 file — additive, no version bump needed. */
  blockEntities: WorldFileBlockEntity[];
  /** Absent/empty on every pre-M53.5 file — additive, no version bump needed
   *  (same treatment as `blockEntities` at M51.6). */
  mobs: WorldFileMob[];
}

const WORLD_TYPES = new Set(['normal', 'flat', 'atlas', 'earth']);
const GAME_MODES = new Set(['survival', 'creative']);
const DIFFICULTIES = new Set(['peaceful', 'easy', 'normal', 'hard']);

/** RLE-encode a 4096-cell section to a flat `[count, state, …]` run list. */
export function encodeSectionRuns(states: Uint16Array): number[] {
  const out: number[] = [];
  let i = 0;
  while (i < states.length) {
    const v = states[i]!;
    let n = 1;
    while (i + n < states.length && states[i + n] === v && n < 65535) n++;
    out.push(n, v);
    i += n;
  }
  return out;
}

/** Decode a run list back to a full 4096-cell section (throws if it doesn't fill). */
export function decodeSectionRuns(runs: readonly number[]): Uint16Array {
  const out = new Uint16Array(SECTION_CELLS);
  let p = 0;
  for (let i = 0; i + 1 < runs.length; i += 2) {
    const n = runs[i]!;
    const v = runs[i + 1]!;
    if (!Number.isInteger(n) || n < 0 || p + n > SECTION_CELLS) throw new Error('worldfile: bad section run length');
    out.fill(v, p, p + n);
    p += n;
  }
  if (p !== SECTION_CELLS) throw new Error(`worldfile: section runs cover ${p}/${SECTION_CELLS} cells`);
  return out;
}

/** Serialize a world to `.tfworld` JSONL text (header line + one line per section
 *  + one line per block entity). */
export function serializeWorldFile(file: WorldFile): string {
  const header = { kind: 'tfworld', version: WORLDFILE_VERSION, meta: file.meta, clock: file.clock };
  const lines = [JSON.stringify(header)];
  for (const s of file.sections) {
    lines.push(JSON.stringify({
      kind: 'section', dim: s.dim, cx: s.cx, cz: s.cz, sy: s.sy,
      rle: s.states ? encodeSectionRuns(s.states) : [],
    }));
  }
  for (const be of file.blockEntities) {
    lines.push(JSON.stringify({ kind: 'blockEntity', dim: be.dim, x: be.x, y: be.y, z: be.z, type: be.type, data: be.data }));
  }
  for (const m of file.mobs) {
    lines.push(JSON.stringify({
      kind: 'mob', dim: m.dim, x: m.x, y: m.y, z: m.z, type: m.type, health: m.health, baby: m.baby,
      ...(m.genome !== undefined ? { genome: m.genome } : {}),
    }));
  }
  return lines.join('\n') + '\n';
}

function asInt(v: unknown, what: string): number {
  if (typeof v !== 'number' || !Number.isInteger(v)) throw new Error(`worldfile: ${what} must be an integer`);
  return v;
}
function asStr(v: unknown, what: string): string {
  if (typeof v !== 'string') throw new Error(`worldfile: ${what} must be a string`);
  return v;
}

function parseMeta(raw: unknown): WorldFileMeta {
  if (typeof raw !== 'object' || raw === null) throw new Error('worldfile: missing meta');
  const m = raw as Record<string, unknown>;
  const worldType = asStr(m.worldType, 'meta.worldType');
  const gameMode = asStr(m.gameMode, 'meta.gameMode');
  const difficulty = asStr(m.difficulty, 'meta.difficulty');
  if (!WORLD_TYPES.has(worldType)) throw new Error(`worldfile: bad worldType '${worldType}'`);
  if (!GAME_MODES.has(gameMode)) throw new Error(`worldfile: bad gameMode '${gameMode}'`);
  if (!DIFFICULTIES.has(difficulty)) throw new Error(`worldfile: bad difficulty '${difficulty}'`);
  return {
    id: asStr(m.id, 'meta.id'),
    name: asStr(m.name, 'meta.name'),
    seed: asStr(m.seed, 'meta.seed'),
    worldType: worldType as WorldFileMeta['worldType'],
    gameMode: gameMode as WorldFileMeta['gameMode'],
    difficulty: difficulty as WorldFileMeta['difficulty'],
    hardcore: m.hardcore === true,
    flatPreset: typeof m.flatPreset === 'string' ? m.flatPreset : '',
  };
}

function parseClock(raw: unknown): WorldFileClock | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== 'object') throw new Error('worldfile: bad clock');
  const c = raw as Record<string, unknown>;
  const w = (c.weather ?? {}) as Record<string, unknown>;
  return {
    tickCount: asInt(c.tickCount, 'clock.tickCount'),
    dayTime: asInt(c.dayTime, 'clock.dayTime'),
    weather: {
      raining: w.raining === true,
      thundering: w.thundering === true,
      rainCountdown: typeof w.rainCountdown === 'number' ? w.rainCountdown : 0,
      thunderCountdown: typeof w.thunderCountdown === 'number' ? w.thunderCountdown : 0,
    },
  };
}

/** Parse `.tfworld` JSONL text back into a world (throws on any malformed line). */
export function parseWorldFile(text: string): WorldFile {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) throw new Error('worldfile: empty file');
  const header = JSON.parse(lines[0]!) as Record<string, unknown>;
  if (header.kind !== 'tfworld') throw new Error('worldfile: first line is not a tfworld header');
  if (header.version !== WORLDFILE_VERSION) throw new Error(`worldfile: unsupported version ${String(header.version)}`);
  const meta = parseMeta(header.meta);
  const clock = parseClock(header.clock);
  const sections: WorldFileSection[] = [];
  const blockEntities: WorldFileBlockEntity[] = [];
  const mobs: WorldFileMob[] = [];
  for (let i = 1; i < lines.length; i++) {
    const rec = JSON.parse(lines[i]!) as Record<string, unknown>;
    if (rec.kind === 'section') {
      const runs = rec.rle;
      if (!Array.isArray(runs)) throw new Error(`worldfile: line ${i + 1} missing rle`);
      sections.push({
        dim: asStr(rec.dim, 'section.dim'),
        cx: asInt(rec.cx, 'section.cx'),
        cz: asInt(rec.cz, 'section.cz'),
        sy: asInt(rec.sy, 'section.sy'),
        states: runs.length ? decodeSectionRuns(runs as number[]) : null,
      });
    } else if (rec.kind === 'blockEntity') {
      blockEntities.push({
        dim: asStr(rec.dim, 'blockEntity.dim'),
        x: asInt(rec.x, 'blockEntity.x'),
        y: asInt(rec.y, 'blockEntity.y'),
        z: asInt(rec.z, 'blockEntity.z'),
        type: asStr(rec.type, 'blockEntity.type'),
        data: rec.data,
      });
    } else if (rec.kind === 'mob') {
      mobs.push({
        dim: asStr(rec.dim, 'mob.dim'),
        x: asInt(rec.x, 'mob.x'),
        y: asInt(rec.y, 'mob.y'),
        z: asInt(rec.z, 'mob.z'),
        type: asStr(rec.type, 'mob.type'),
        health: typeof rec.health === 'number' ? rec.health : asInt(rec.health, 'mob.health'),
        baby: rec.baby === true,
        ...(typeof rec.genome === 'string' ? { genome: rec.genome } : {}),
      });
    } else {
      throw new Error(`worldfile: line ${i + 1} is not a section, a blockEntity, or a mob`);
    }
  }
  return { meta, clock, sections, blockEntities, mobs };
}
